/**
 * Keyword glossary data for the MTG Rules Reference.
 * Contains all evergreen keyword abilities with reminder text,
 * explanations, rules references, and categories.
 *
 * CommonJS module for compatibility with both backend (require) and
 * frontend (import via Webpack/Babel interop).
 *
 * @module rules/keywordData
 */

const KEYWORD_CATEGORIES = {
  EVASION: 'Evasion',
  COMBAT: 'Combat',
  PROTECTION: 'Protection',
  STATIC: 'Static',
  TRIGGERED: 'Triggered',
  ACTIVATED: 'Activated',
};

const KEYWORD_THEMES = {
  EVERGREEN: 'Evergreen',
  GRAVEYARD: 'Graveyard',
  COST_REDUCTION: 'Cost Reduction',
  COMBAT: 'Combat',
  CARD_FILTERING: 'Card Filtering',
  PROTECTION: 'Protection',
  RECURSION: 'Recursion',
  ALTERNATIVE_CASTING: 'Alternative Casting',
  COUNTERS: 'Counters',
  EVASION: 'Evasion',
  MANA_AND_RESOURCES: 'Mana & Resources',
  FACE_DOWN_AND_TRANSFORM: 'Face-Down & Transform',
  MISCELLANEOUS: 'Miscellaneous',
};

const KEYWORD_GLOSSARY = [
  {
    name: 'Flying',
    reminder: 'This creature can\'t be blocked except by creatures with flying or reach.',
    explanation: 'Flying is an evasion ability that makes a creature very difficult to block. Only creatures that also have flying or the reach ability can block a flyer. Flying does not affect how a creature can block. A creature with flying can block both flying and non-flying attackers.',
    rulesReference: '702.9',
    category: KEYWORD_CATEGORIES.EVASION,
    theme: KEYWORD_THEMES.EVASION,
  },
  {
    name: 'Trample',
    reminder: 'This creature can deal excess combat damage to the player or planeswalker it\'s attacking.',
    explanation: 'When a creature with trample is blocked, it must assign lethal damage to each blocker (in order, if multiply blocked), but any remaining damage can be assigned to the defending player or planeswalker. Without trample, all combat damage from a blocked creature is assigned to blockers, even if the total power exceeds their combined toughness.',
    rulesReference: '702.19',
    category: KEYWORD_CATEGORIES.COMBAT,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Deathtouch',
    reminder: 'Any amount of damage this deals to a creature is enough to destroy it.',
    explanation: 'A creature dealt any amount of damage by a source with deathtouch is destroyed as a state-based action, regardless of its toughness. This means a 1/1 with deathtouch can kill an enormous creature. When combined with trample, the attacking creature only needs to assign 1 damage to each blocker (since that counts as lethal) and the rest tramples over.',
    rulesReference: '702.2',
    category: KEYWORD_CATEGORIES.COMBAT,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Haste',
    reminder: 'This creature can attack and tap as soon as it comes under your control.',
    explanation: 'Normally, a creature cannot attack or use activated abilities with the tap symbol the turn it comes under your control (informally called "summoning sickness"). Haste removes this restriction. Note that summoning sickness only prevents attacking and activating abilities with the tap/untap symbol. Other abilities work normally regardless.',
    rulesReference: '702.10',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.EVERGREEN,
  },
  {
    name: 'First Strike',
    reminder: 'This creature deals combat damage before creatures without first strike.',
    explanation: 'During the combat damage step, creatures with first strike deal their damage in a special first-strike combat damage step, before regular combat damage. If a first-strike creature kills a blocker or attacker before regular damage, the destroyed creature deals no damage in return. If no creatures have first strike or double strike, the first-strike combat damage step is skipped entirely.',
    rulesReference: '702.7',
    category: KEYWORD_CATEGORIES.COMBAT,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Double Strike',
    reminder: 'This creature deals both first-strike and regular combat damage.',
    explanation: 'A creature with double strike deals combat damage in both the first-strike combat damage step and the regular combat damage step. This effectively means it deals damage twice. Double strike is not the same as having first strike. It includes first strike but also deals damage again at normal speed.',
    rulesReference: '702.4',
    category: KEYWORD_CATEGORIES.COMBAT,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Vigilance',
    reminder: 'Attacking doesn\'t cause this creature to tap.',
    explanation: 'Normally, attacking requires a creature to tap. A creature with vigilance can attack without tapping, meaning it is still available to block during the opponent\'s next turn or to use tap abilities. This makes vigilance creatures excellent at both offense and defense.',
    rulesReference: '702.20',
    category: KEYWORD_CATEGORIES.COMBAT,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Reach',
    reminder: 'This creature can block creatures with flying.',
    explanation: 'Reach allows a creature without flying to block creatures that have flying. A creature with reach can still be blocked normally by any creature. Reach is essentially the ground-based answer to flying.',
    rulesReference: '702.17',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.EVERGREEN,
  },
  {
    name: 'Lifelink',
    reminder: 'Damage dealt by this creature also causes you to gain that much life.',
    explanation: 'Whenever a creature with lifelink deals any damage (combat or non-combat), its controller gains that much life simultaneously. If multiple creatures with lifelink deal damage at the same time, the life is gained simultaneously. Lifelink is not a triggered ability. The life gain happens as part of the damage event itself.',
    rulesReference: '702.15',
    category: KEYWORD_CATEGORIES.COMBAT,
    theme: KEYWORD_THEMES.EVERGREEN,
  },
  {
    name: 'Menace',
    reminder: 'This creature can\'t be blocked except by two or more creatures.',
    explanation: 'Menace is an evasion ability that requires the defending player to commit at least two creatures to block the attacker. If the defending player cannot or chooses not to block with two or more creatures, the menace creature is unblocked. This makes menace creatures effective at getting through when opponents have few available blockers.',
    rulesReference: '702.110',
    category: KEYWORD_CATEGORIES.EVASION,
    theme: KEYWORD_THEMES.EVASION,
  },
  {
    name: 'Hexproof',
    reminder: 'This permanent can\'t be the target of spells or abilities your opponents control.',
    explanation: 'Hexproof prevents opponents from targeting the permanent with any spell or ability. The permanent\'s controller can still target it. Hexproof does not prevent effects that do not target (such as board wipes like "destroy all creatures"). Some cards grant "hexproof from [quality]" which only prevents targeting by sources of that quality.',
    rulesReference: '702.11',
    category: KEYWORD_CATEGORIES.PROTECTION,
    theme: KEYWORD_THEMES.PROTECTION,
  },
  {
    name: 'Shroud',
    reminder: 'This permanent can\'t be the target of spells or abilities.',
    explanation: 'Shroud prevents ALL targeting (from both opponents and the permanent\'s controller). This means you cannot enchant, equip (after it enters), or target your own shrouded creature with pump spells. Shroud has been largely replaced by hexproof in newer sets but still appears on older cards.',
    rulesReference: '702.18',
    category: KEYWORD_CATEGORIES.PROTECTION,
    theme: KEYWORD_THEMES.PROTECTION,
  },
  {
    name: 'Indestructible',
    reminder: 'Effects that say "destroy" don\'t destroy this permanent. This creature can\'t be destroyed by lethal damage.',
    explanation: 'An indestructible permanent cannot be destroyed by "destroy" effects or by lethal damage (damage greater than or equal to its toughness). However, indestructible does NOT prevent: sacrifice, exile, having toughness reduced to 0 or less (state-based action), being put into the graveyard due to the legend rule, or being countered. A creature with indestructible still has damage marked on it.',
    rulesReference: '702.12',
    category: KEYWORD_CATEGORIES.PROTECTION,
    theme: KEYWORD_THEMES.PROTECTION,
  },
  {
    name: 'Flash',
    reminder: 'You may cast this spell any time you could cast an instant.',
    explanation: 'Flash allows a card to be cast at instant speed, during any phase or step when you have priority, including during an opponent\'s turn. Without flash, creatures, enchantments, artifacts, sorceries, planeswalkers, and battles can only be cast during your main phase when the stack is empty.',
    rulesReference: '702.8',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.EVERGREEN,
  },
  {
    name: 'Defender',
    reminder: 'This creature can\'t attack.',
    explanation: 'A creature with defender simply cannot be declared as an attacker. It can still block normally. Defender is most commonly found on Wall creature types. Some cards have abilities that allow creatures with defender to attack or that remove defender from creatures.',
    rulesReference: '702.3',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.EVERGREEN,
  },
  {
    name: 'Ward',
    reminder: 'Whenever this permanent becomes the target of a spell or ability an opponent controls, counter it unless that player pays [cost].',
    explanation: 'Ward is a triggered ability that acts as a shield against opponent targeting. When an opponent targets a permanent with ward, a triggered ability goes on the stack. If the opponent does not pay the ward cost, their spell or ability is countered. Ward costs can be mana, life, or other resources. Unlike hexproof, ward does not prevent targeting. It adds a cost to it.',
    rulesReference: '702.21',
    category: KEYWORD_CATEGORIES.PROTECTION,
    theme: KEYWORD_THEMES.PROTECTION,
  },
  {
    name: 'Equip',
    reminder: 'Attach to target creature you control. Equip only as a sorcery.',
    explanation: 'Equip is an activated ability found on Equipment artifact cards. Paying the equip cost lets you attach the Equipment to a target creature you control. Equipment stays on the battlefield when the equipped creature leaves. You can move Equipment to a different creature by paying the equip cost again. Equip can only be activated at sorcery speed (during your main phase, stack empty).',
    rulesReference: '702.6',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.EVERGREEN,
  },
  {
    name: 'Protection',
    reminder: 'This permanent can\'t be damaged, enchanted, equipped, blocked, or targeted by anything with the stated quality.',
    explanation: 'Protection from [quality] prevents four things, remembered by the acronym DEBT: Damage from sources with that quality is prevented, the permanent cannot be Enchanted/Equipped by permanents with that quality, it cannot be Blocked by creatures with that quality, and it cannot be Targeted by spells or abilities from sources with that quality. Protection does NOT prevent board-wide effects that don\'t target.',
    rulesReference: '702.16',
    category: KEYWORD_CATEGORIES.PROTECTION,
    theme: KEYWORD_THEMES.PROTECTION,
  },
  {
    name: 'Prowess',
    reminder: 'Whenever you cast a noncreature spell, this creature gets +1/+1 until end of turn.',
    explanation: 'Prowess is a triggered ability that rewards casting noncreature spells. Each noncreature spell you cast triggers prowess separately. If a creature has multiple instances of prowess, each instance triggers individually. The +1/+1 bonus lasts until end of turn.',
    rulesReference: '702.107',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Cycling',
    reminder: 'You may pay the cycling cost and discard this card to draw a card.',
    explanation: 'Cycling is an activated ability that can be used from your hand. By paying the cycling cost and discarding the card, you draw a new card. This lets you trade away cards that are not useful in the current game state. Cycling is activated from the hand and uses the stack, so it can be responded to. Some cards have "cycling triggers" that do something when you cycle them.',
    rulesReference: '702.28',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.CARD_FILTERING,
  },
  {
    name: 'Landfall',
    reminder: 'Whenever a land enters the battlefield under your control, [effect].',
    explanation: 'Landfall is an ability word (not a keyword ability in the strict rules sense) that marks triggered abilities that trigger whenever a land enters the battlefield under your control. Landfall triggers on any land entering, not just lands you play. Fetch lands, ramp spells, and other effects that put lands onto the battlefield will all trigger landfall.',
    rulesReference: '702.171',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.MANA_AND_RESOURCES,
  },
  {
    name: 'Flashback',
    reminder: 'You may cast this card from your graveyard for its flashback cost. Then exile it.',
    explanation: 'Flashback lets you cast a spell a second time from your graveyard by paying its flashback cost instead of its mana cost. After the spell resolves (or is countered, or leaves the stack for any reason), it is exiled instead of returning to the graveyard. You can only use flashback if the card is in your graveyard.',
    rulesReference: '702.33',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.GRAVEYARD,
  },
  {
    name: 'Unearth',
    reminder: 'Return this card from your graveyard to the battlefield. It gains haste. Exile it at the beginning of the next end step or if it would leave the battlefield.',
    explanation: 'Unearth is an activated ability you use from the graveyard. It returns the creature to the battlefield with haste, but it is exiled at the beginning of the next end step or if it would leave the battlefield for any reason. This lets you get one last attack or use of a creature before it is gone for good.',
    rulesReference: '702.83',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.GRAVEYARD,
  },
  {
    name: 'Embalm',
    reminder: 'Exile this card from your graveyard: Create a token that\'s a copy of it, except it\'s a white Zombie in addition to its other types with no mana cost. Embalm only as a sorcery.',
    explanation: 'Embalm is an activated ability usable only from the graveyard and only at sorcery speed. It exiles the creature card and creates a token copy that is white, a Zombie in addition to its other types, and has no mana cost. The token retains all other abilities of the original card.',
    rulesReference: '702.128',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.GRAVEYARD,
  },
  {
    name: 'Eternalize',
    reminder: 'Exile this card from your graveyard: Create a token that\'s a copy of it, except it\'s a 4/4 black Zombie in addition to its other types with no mana cost. Eternalize only as a sorcery.',
    explanation: 'Eternalize works like embalm but the token is always a 4/4 black Zombie. It is an activated ability usable only from the graveyard at sorcery speed. The original card is exiled and a token copy is created that retains all abilities but is 4/4, black, and a Zombie in addition to its other types.',
    rulesReference: '702.133',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.GRAVEYARD,
  },
  {
    name: 'Escape',
    reminder: 'You may cast this card from your graveyard for its escape cost.',
    explanation: 'Escape lets you cast a card from your graveyard by paying its escape cost, which typically involves paying mana and exiling a number of other cards from your graveyard. You can escape a card any number of times as long as you can pay the cost. A creature that escapes may get additional +1/+1 counters as specified.',
    rulesReference: '702.138',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.GRAVEYARD,
  },
  {
    name: 'Disturb',
    reminder: 'You may cast this card transformed from your graveyard for its disturb cost.',
    explanation: 'Disturb is found on double-faced cards and lets you cast the back face from your graveyard by paying the disturb cost. The card enters the battlefield transformed. If the permanent would be put into the graveyard from the battlefield, it is exiled instead. This gives the card a second life as a different permanent.',
    rulesReference: '702.146',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.GRAVEYARD,
  },
  {
    name: 'Encore',
    reminder: 'Exile this card from your graveyard: For each opponent, create a token copy that attacks that opponent this turn if able. They gain haste. Sacrifice them at the beginning of the next end step. Activate only as a sorcery.',
    explanation: 'Encore is an activated ability usable from the graveyard at sorcery speed. It creates a token copy for each opponent, each of which must attack that specific opponent if able. The tokens gain haste and are sacrificed at the beginning of the next end step. The original card is exiled as part of the cost.',
    rulesReference: '702.141',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.GRAVEYARD,
  },
  {
    name: 'Retrace',
    reminder: 'You may cast this card from your graveyard by discarding a land card in addition to paying its other costs.',
    explanation: 'Retrace lets you cast a spell from your graveyard repeatedly by discarding a land card as an additional cost each time. The spell goes on the stack normally and, if it resolves, returns to the graveyard where it can be retraced again. This turns excess lands into repeated spell effects.',
    rulesReference: '702.80',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.GRAVEYARD,
  },
  {
    name: 'Jump-start',
    reminder: 'You may cast this card from your graveyard by discarding a card in addition to paying its other costs. Then exile it.',
    explanation: 'Jump-start lets you cast an instant or sorcery from your graveyard by discarding a card as an additional cost. After it resolves (or leaves the stack), it is exiled instead of returning to the graveyard. Unlike retrace, you can discard any card (not just a land), but you can only use jump-start once.',
    rulesReference: '702.133a',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.GRAVEYARD,
  },
  {
    name: 'Dredge',
    reminder: 'If you would draw a card, you may mill N cards instead. If you do, return this card from your graveyard to your hand.',
    explanation: 'Dredge is a replacement effect that replaces drawing a card. Instead of drawing, you put the specified number of cards from the top of your library into your graveyard and return the dredge card to your hand. Dredge is one of the most powerful graveyard mechanics ever printed because it fills your graveyard while retrieving cards.',
    rulesReference: '702.52',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.CARD_FILTERING,
  },
  {
    name: 'Persist',
    reminder: 'When this creature dies, if it had no -1/-1 counters on it, return it to the battlefield under its owner\'s control with a -1/-1 counter on it.',
    explanation: 'Persist is a triggered ability that returns a creature to the battlefield when it dies, but only if it had no -1/-1 counters. It comes back with a -1/-1 counter, which prevents persist from triggering again unless the counter is removed. Persist and undying are complementary mechanics.',
    rulesReference: '702.78',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.RECURSION,
  },
  {
    name: 'Undying',
    reminder: 'When this creature dies, if it had no +1/+1 counters on it, return it to the battlefield under its owner\'s control with a +1/+1 counter on it.',
    explanation: 'Undying is the mirror of persist. When the creature dies without any +1/+1 counters, it returns to the battlefield with a +1/+1 counter. The creature comes back slightly larger but cannot trigger undying again unless the counter is removed.',
    rulesReference: '702.93',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.RECURSION,
  },
  {
    name: 'Aftermath',
    reminder: 'Cast this spell only from your graveyard. Then exile it.',
    explanation: 'Aftermath is found on the second half of split cards. The aftermath half can only be cast from the graveyard, not from your hand. After it resolves, it is exiled. The first half is cast normally from your hand. This gives split cards flexibility across two different game zones.',
    rulesReference: '702.127',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.GRAVEYARD,
  },
  {
    name: 'Madness',
    reminder: 'If you discard this card, discard it into exile. When you do, cast it for its madness cost or put it into your graveyard.',
    explanation: 'Madness turns the drawback of discarding into an opportunity. When you discard a card with madness, instead of going to the graveyard it goes to exile, and you get a triggered ability that lets you cast it for its madness cost (often cheaper). If you choose not to cast it, it goes to the graveyard.',
    rulesReference: '702.34',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Scavenge',
    reminder: 'Exile this card from your graveyard: Put a number of +1/+1 counters equal to this card\'s power on target creature. Scavenge only as a sorcery.',
    explanation: 'Scavenge is an activated ability used from the graveyard at sorcery speed. You exile the creature card and put +1/+1 counters equal to its power on a target creature. This lets dead creatures provide a permanent buff to your surviving forces.',
    rulesReference: '702.96',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.GRAVEYARD,
  },
  {
    name: 'Surveil',
    reminder: 'Look at the top N cards of your library. Put any number of them into your graveyard and the rest on top of your library in any order.',
    explanation: 'Surveil is similar to scry but puts unwanted cards into the graveyard instead of the bottom of your library. This serves double duty: it improves your draws like scry while also filling your graveyard for synergies with flashback, escape, delve, and other graveyard-based mechanics.',
    rulesReference: '702.137',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.CARD_FILTERING,
  },
  {
    name: 'Investigate',
    reminder: 'Create a Clue token. (It\'s an artifact with "2, Sacrifice this artifact: Draw a card.")',
    explanation: 'Investigate creates a Clue artifact token. Clues can be sacrificed for 2 mana to draw a card, providing card advantage at the cost of tempo. Clue tokens are artifacts, so they also synergize with artifact-matters cards. Investigate is typically a keyword action found on triggered abilities or spells.',
    rulesReference: '702.35',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.CARD_FILTERING,
  },
  {
    name: 'Delve',
    reminder: 'Each card you exile from your graveyard while casting this spell pays for {1}.',
    explanation: 'Delve lets you exile cards from your graveyard to pay for generic mana in a spell\'s cost. Each card exiled pays for one generic mana. This can make expensive spells very cheap if you have a full graveyard. Delve is one of the most powerful cost-reduction mechanics and has led to several banned cards.',
    rulesReference: '702.66',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Recover',
    reminder: 'When a creature is put into your graveyard from the battlefield, you may pay the recover cost. If you do, return this card from your graveyard to your hand. Otherwise, exile this card.',
    explanation: 'Recover is a triggered ability that activates while the card is in your graveyard. When another creature you control dies, you may pay the recover cost to return the recover card to your hand. If you choose not to pay, the recover card is exiled. This provides repeatable card advantage tied to creature deaths.',
    rulesReference: '702.58',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.RECURSION,
  },
  {
    name: 'Haunt',
    reminder: 'When this creature dies, exile it haunting target creature.',
    explanation: 'Haunt is a triggered ability. When a creature with haunt dies (or a spell with haunt resolves), it is exiled attached to ("haunting") a target creature. When the haunted creature dies, the haunt ability triggers again, giving you a second use of the effect. Haunt connects two creature deaths to get double value.',
    rulesReference: '702.54',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.RECURSION,
  },
  {
    name: 'Kicker',
    reminder: 'You may pay an additional cost as you cast this spell.',
    explanation: 'Kicker is an optional additional cost you can pay when casting a spell. If you pay the kicker cost, the spell gains an enhanced effect, often a stronger version of its base ability or an entirely new bonus. You choose whether to kick the spell as you cast it. You cannot change your mind later.',
    rulesReference: '702.32',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.COST_REDUCTION,
  },
  {
    name: 'Multikicker',
    reminder: 'You may pay an additional cost any number of times as you cast this spell.',
    explanation: 'Multikicker is a variant of kicker that can be paid any number of times. Each time you pay the multikicker cost, the spell\'s effect scales accordingly. This allows you to invest as much mana as you have available for a proportionally larger effect.',
    rulesReference: '702.32b',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.COST_REDUCTION,
  },
  {
    name: 'Buyback',
    reminder: 'You may pay an additional cost as you cast this spell. If you do, put it into your hand as it resolves.',
    explanation: 'Buyback is an optional additional cost. If you pay it, the spell returns to your hand instead of going to the graveyard after it resolves. This lets you cast the same spell repeatedly, turn after turn, as long as you keep paying the buyback cost. Buyback only works if the spell resolves successfully.',
    rulesReference: '702.26',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Overload',
    reminder: 'You may cast this spell for its overload cost. If you do, change its text by replacing all instances of "target" with "each."',
    explanation: 'Overload is an alternative cost that changes a targeted spell into a mass effect. When you pay the overload cost, every instance of "target" in the spell\'s text is replaced with "each," turning a single-target spell into one that affects all valid objects. The overloaded spell no longer targets, so it gets around hexproof and shroud.',
    rulesReference: '702.95',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Evoke',
    reminder: 'You may cast this spell for its evoke cost. If you do, it\'s sacrificed when it enters the battlefield.',
    explanation: 'Evoke is an alternative cost for creatures that lets you cast them cheaply in exchange for immediately sacrificing them when they enter the battlefield. This is useful when you only want the creature\'s enters-the-battlefield trigger. The sacrifice is a triggered ability, so it can be responded to.',
    rulesReference: '702.73',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Convoke',
    reminder: 'Your creatures can help cast this spell. Each creature you tap while casting this spell pays for {1} or one mana of that creature\'s color.',
    explanation: 'Convoke lets you tap your creatures to help pay for a spell. Each tapped creature pays for one generic mana or one mana of any of that creature\'s colors. This effectively turns your creatures into mana sources, allowing you to cast expensive spells earlier by tapping your board.',
    rulesReference: '702.50',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.COST_REDUCTION,
  },
  {
    name: 'Improvise',
    reminder: 'Your artifacts can help cast this spell. Each artifact you tap while casting this spell pays for {1}.',
    explanation: 'Improvise is similar to convoke but uses artifacts instead of creatures. Each artifact you tap while casting the spell pays for one generic mana. Unlike convoke, improvise can only pay for generic mana costs, not colored mana. This rewards having many artifacts on the battlefield.',
    rulesReference: '702.125',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.COST_REDUCTION,
  },
  {
    name: 'Emerge',
    reminder: 'You may cast this spell by sacrificing a creature and paying the emerge cost reduced by that creature\'s mana value.',
    explanation: 'Emerge is an alternative cost that lets you sacrifice a creature to reduce the casting cost. The emerge cost is reduced by the sacrificed creature\'s mana value. This creates a transformation effect where a smaller creature becomes a larger one, and the cost discount makes expensive creatures more affordable.',
    rulesReference: '702.118',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Affinity',
    reminder: 'This spell costs {1} less to cast for each [specified permanent type] you control.',
    explanation: 'Affinity reduces the generic mana cost of a spell by one for each permanent of the specified type you control. Most commonly seen as "affinity for artifacts," where each artifact you control makes the spell one generic mana cheaper. Affinity can reduce the cost to zero but cannot reduce colored mana requirements.',
    rulesReference: '702.40',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.COST_REDUCTION,
  },
  {
    name: 'Spectacle',
    reminder: 'You may cast this spell for its spectacle cost rather than its mana cost if an opponent lost life this turn.',
    explanation: 'Spectacle is an alternative cost that becomes available when an opponent has lost life during the current turn. The spectacle cost is usually cheaper than the regular mana cost, rewarding aggressive play. Any life loss counts: combat damage, shocklands, fetch lands, or direct damage.',
    rulesReference: '702.136',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Surge',
    reminder: 'You may cast this spell for its surge cost if you or a teammate has cast another spell this turn.',
    explanation: 'Surge is an alternative cost available when you or a teammate has already cast a spell during the current turn. The surge cost is typically cheaper than the normal cost, encouraging you to chain spells together. In multiplayer formats, a teammate casting a spell also enables surge.',
    rulesReference: '702.116',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Miracle',
    reminder: 'You may cast this card for its miracle cost when you draw it if it\'s the first card you drew this turn.',
    explanation: 'Miracle is an alternative cost that can only be used when you draw the card as the first card drawn in a turn. When you draw a miracle card as your first draw, you may reveal it and cast it immediately for its miracle cost (usually much cheaper). The reveal must happen as the card is drawn. You cannot look at it first and decide later.',
    rulesReference: '702.93',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Foretell',
    reminder: 'During your turn, you may pay {2} and exile this card from your hand face down. Cast it on a later turn for its foretell cost.',
    explanation: 'Foretell lets you pay 2 mana during your turn to exile a card from your hand face down. On a later turn, you can cast it from exile for its foretell cost, which is usually cheaper than the regular cost. This splits the mana investment across two turns and creates hidden information for your opponent.',
    rulesReference: '702.142',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Plot',
    reminder: 'You may pay the plot cost and exile this card from your hand. Cast it as a sorcery on a later turn without paying its mana cost.',
    explanation: 'Plot lets you exile a card from your hand by paying its plot cost. On a later turn, you can cast it without paying its mana cost, but only as a sorcery. This allows you to pre-pay for spells and then deploy them for free later, enabling powerful turns where you cast multiple spells.',
    rulesReference: '702.164',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Entwine',
    reminder: 'You may pay the entwine cost in addition to any other costs as you cast this spell. If you do, choose both modes.',
    explanation: 'Entwine appears on modal spells that normally let you choose one mode. By paying the entwine cost in addition to the spell\'s mana cost, you get to use both modes instead of just one. This turns a flexible but modest spell into a powerful two-for-one effect.',
    rulesReference: '702.41',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.COST_REDUCTION,
  },
  {
    name: 'Splice',
    reminder: 'As you cast an [specified subtype] spell, you may reveal this card from your hand and pay its splice cost. If you do, add this card\'s effects to that spell.',
    explanation: 'Splice lets you graft one spell\'s effect onto another as you cast it. Most commonly "splice onto Arcane," you reveal a splice card from your hand and pay the splice cost to add its text to the spell you\'re casting. The spliced card stays in your hand, so you can reuse it repeatedly.',
    rulesReference: '702.46',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Cascade',
    reminder: 'When you cast this spell, exile cards from the top of your library until you exile a nonland card that costs less. You may cast it without paying its mana cost. Put the exiled cards on the bottom in a random order.',
    explanation: 'Cascade triggers when you cast a spell with cascade, exiling cards from the top of your library until you hit a nonland card with a lower mana value. You may cast that card for free, then put all exiled cards on the bottom in a random order. Cascade is extremely powerful because it provides a free spell every time.',
    rulesReference: '702.84',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Suspend',
    reminder: 'Rather than cast this card from your hand, pay its suspend cost and exile it with N time counters on it. At the beginning of your upkeep, remove a time counter. When the last is removed, cast it without paying its mana cost. It has haste.',
    explanation: 'Suspend lets you exile a card from your hand with time counters by paying a reduced cost. Each upkeep removes a counter, and when the last counter is removed, you cast the spell for free and it gains haste if it\'s a creature. Suspend trades immediate impact for a cheaper cost, creating a delayed threat your opponent can see coming.',
    rulesReference: '702.61',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Storm',
    reminder: 'When you cast this spell, copy it for each spell cast before it this turn.',
    explanation: 'Storm is a triggered ability that copies the spell for each other spell cast earlier in the same turn. If three spells were cast before the storm spell, you get three copies plus the original for four total. Storm is one of the most powerful mechanics ever printed and has led to numerous bans across formats.',
    rulesReference: '702.39',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Rebound',
    reminder: 'If you cast this spell from your hand, exile it as it resolves. At the beginning of your next upkeep, you may cast this card from exile without paying its mana cost.',
    explanation: 'Rebound lets you cast an instant or sorcery a second time for free. When the spell resolves (if cast from your hand), it is exiled instead of going to the graveyard. At the beginning of your next upkeep, you may cast it again from exile without paying its mana cost. If you choose not to cast it, it goes to the graveyard.',
    rulesReference: '702.87',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Ripple',
    reminder: 'When you cast this spell, you may reveal the top N cards of your library. You may cast any of those cards with the same name without paying their mana costs. Put the rest on the bottom of your library.',
    explanation: 'Ripple triggers when you cast a spell with ripple, letting you reveal cards from the top of your library and cast any copies with the same name for free. Ripple is most effective when your deck contains many copies of the same card (as in Relentless Rats decks). Each rippled copy also triggers ripple, potentially chaining multiple casts.',
    rulesReference: '702.59',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Split Second',
    reminder: 'As long as this spell is on the stack, players can\'t cast spells or activate abilities that aren\'t mana abilities.',
    explanation: 'Split second prevents players from casting spells or activating non-mana abilities while the split second spell is on the stack. This effectively makes the spell uncounterable by conventional means (since counterspells can\'t be cast). However, triggered abilities still trigger, mana abilities still work, and special actions (like morph) can still be taken.',
    rulesReference: '702.60',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Morph',
    reminder: 'You may cast this card face down as a 2/2 creature for {3}. Turn it face up any time for its morph cost.',
    explanation: 'Morph lets you cast a creature face down as a generic 2/2 with no name, types, abilities, or mana cost for 3 generic mana. At any time you have priority, you can turn it face up by paying the morph cost. Turning a creature face up is a special action that does not use the stack and cannot be responded to. Your opponent does not know what it is until you flip it.',
    rulesReference: '702.36',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.FACE_DOWN_AND_TRANSFORM,
  },
  {
    name: 'Megamorph',
    reminder: 'You may cast this card face down as a 2/2 creature for {3}. Turn it face up any time for its megamorph cost and put a +1/+1 counter on it.',
    explanation: 'Megamorph works identically to morph except that when the creature is turned face up, it also gets a +1/+1 counter. This provides a small bonus for flipping the creature, making the face-up version slightly stronger than a regular morph flip.',
    rulesReference: '702.36b',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.FACE_DOWN_AND_TRANSFORM,
  },
  {
    name: 'Manifest',
    reminder: 'Put the top card of your library onto the battlefield face down as a 2/2 creature. If it\'s a creature card, you may turn it face up at any time by paying its mana cost.',
    explanation: 'Manifest takes the top card of your library and puts it on the battlefield face down as a 2/2 creature. If the manifested card is a creature card, you can turn it face up by paying its mana cost. If it is a noncreature card, it stays face down as a 2/2 unless an effect turns it face up. This creates uncertainty for your opponent since any card could be manifested.',
    rulesReference: '701.34',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.FACE_DOWN_AND_TRANSFORM,
  },
  {
    name: 'Disguise',
    reminder: 'You may cast this card face down as a 2/2 creature with ward {2} for {3}. Turn it face up any time for its disguise cost.',
    explanation: 'Disguise is an updated version of morph. Like morph, you cast the creature face down as a 2/2 for 3 generic mana and can turn it face up by paying the disguise cost. The key difference is that disguised creatures have ward {2}, making them harder for opponents to remove while face down.',
    rulesReference: '702.168',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.FACE_DOWN_AND_TRANSFORM,
  },
  {
    name: 'Cloak',
    reminder: 'Put the card onto the battlefield face down as a 2/2 creature with ward {2}. If it\'s a creature card, you may turn it face up at any time by paying its mana cost.',
    explanation: 'Cloak is the disguise equivalent of manifest. It puts a card onto the battlefield face down as a 2/2 creature with ward {2}. If the cloaked card is a creature card, it can be turned face up by paying its mana cost. Cloak combines the hidden information of manifest with the ward {2} protection of disguise.',
    rulesReference: '701.56',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.FACE_DOWN_AND_TRANSFORM,
  },
  {
    name: 'Mutate',
    reminder: 'If you cast this spell for its mutate cost, put it over or under target non-Human creature you own. They mutate into the creature on top plus all abilities from under it.',
    explanation: 'Mutate lets you merge a creature with another non-Human creature you own. You choose to place the mutating creature on top or bottom. The resulting creature has the characteristics of the top card plus all abilities of every card in the merged pile. Mutate triggers "whenever this creature mutates" abilities. The merged creature is one permanent with multiple cards.',
    rulesReference: '702.139',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.FACE_DOWN_AND_TRANSFORM,
  },
  {
    name: 'Bestow',
    reminder: 'If you cast this card for its bestow cost, it\'s an Aura spell with enchant creature. It becomes a creature again if it\'s not attached to a creature.',
    explanation: 'Bestow lets you cast a creature as an Aura that enchants a creature, granting it the bestow creature\'s power, toughness, and abilities. If the enchanted creature leaves the battlefield, the bestow Aura becomes a creature instead of going to the graveyard. This dual nature provides flexibility and resilience.',
    rulesReference: '702.102',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Prototype',
    reminder: 'You may cast this spell with different mana cost, color, and size for its prototype cost.',
    explanation: 'Prototype provides an alternative set of characteristics (mana cost, color, power, toughness) for casting a spell at a lower cost. If cast for the prototype cost, the permanent uses the prototype characteristics. If cast normally, it uses the full characteristics. The permanent retains all its abilities regardless of which way it was cast.',
    rulesReference: '702.160',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Daybound',
    reminder: 'If a player casts no spells during their own turn, it becomes night next turn.',
    explanation: 'Daybound appears on the front face of transforming double-faced cards that track the day/night cycle. A daybound permanent transforms to its nightbound back face when it becomes night. It becomes night if the active player cast no spells during their turn. Daybound permanents enter the battlefield with whichever face matches the current time of day.',
    rulesReference: '702.145',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.FACE_DOWN_AND_TRANSFORM,
  },
  {
    name: 'Nightbound',
    reminder: 'If a player casts at least two spells during their own turn, it becomes day next turn.',
    explanation: 'Nightbound appears on the back face of transforming double-faced cards that use the day/night cycle. A nightbound permanent transforms to its daybound front face when it becomes day. It becomes day if the active player cast two or more spells during their turn. Nightbound permanents cannot be transformed by other effects.',
    rulesReference: '702.145b',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.FACE_DOWN_AND_TRANSFORM,
  },
  {
    name: 'Meld',
    reminder: 'If you own and control both this card and its meld pair, exile them and combine them into the melded permanent.',
    explanation: 'Meld is a mechanic where two specific cards combine into one oversized card. When the meld condition is met (usually a triggered ability), both cards are exiled and returned to the battlefield as a single merged permanent represented by the back face. If the melded permanent leaves the battlefield, both cards go to the appropriate zone separately.',
    rulesReference: '702.140',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.FACE_DOWN_AND_TRANSFORM,
  },
  {
    name: 'Living Weapon',
    reminder: 'When this Equipment enters the battlefield, create a 0/0 black Phyrexian Germ creature token, then attach this to it.',
    explanation: 'Living weapon is a triggered ability on Equipment that automatically creates a 0/0 Germ creature token and attaches the Equipment to it when it enters the battlefield. This means the Equipment arrives ready to fight without needing a separate creature. If the Equipment is moved to another creature, the 0/0 Germ dies.',
    rulesReference: '702.91',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.MISCELLANEOUS,
  },
  {
    name: 'Reconfigure',
    reminder: 'Attach to target creature you control, or unattach. Reconfigure only as a sorcery. While attached, this isn\'t a creature.',
    explanation: 'Reconfigure is found on artifact creatures that can also function as Equipment. By paying the reconfigure cost, the creature attaches to another creature you control (and stops being a creature itself) or detaches (becoming a creature again). This provides flexibility between having an extra body and buffing another creature.',
    rulesReference: '702.151',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.MISCELLANEOUS,
  },
  {
    name: 'Craft',
    reminder: 'Exile this permanent and the specified materials from among permanents you control and/or cards in your graveyard. Return this card transformed.',
    explanation: 'Craft is an activated ability that lets you transform a permanent by exiling it along with specified materials (other permanents or graveyard cards). The card returns to the battlefield transformed, usually into a more powerful version. The materials are consumed (exiled) as part of the cost.',
    rulesReference: '702.163',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.FACE_DOWN_AND_TRANSFORM,
  },
  {
    name: 'Changeling',
    reminder: 'This card is every creature type.',
    explanation: 'Changeling is a characteristic-defining ability that makes a creature every creature type at all times, in all zones. A changeling is simultaneously an Elf, Goblin, Human, Dragon, Sliver, and every other creature type. This makes changelings work with any tribal synergy, fitting into any creature-type-matters strategy.',
    rulesReference: '702.72',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.MISCELLANEOUS,
  },
  {
    name: 'Devoid',
    reminder: 'This card has no color.',
    explanation: 'Devoid is a characteristic-defining ability that makes a card colorless regardless of the colored mana in its mana cost. A card with devoid that costs {1}{B}{G} is still colorless. Devoid affects the card in all zones, not just the battlefield. It was introduced to represent Eldrazi corruption of colored magic.',
    rulesReference: '702.113',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.MISCELLANEOUS,
  },
  {
    name: 'Adapt',
    reminder: 'If this creature has no +1/+1 counters on it, put N +1/+1 counters on it.',
    explanation: 'Adapt is an activated ability that puts a specified number of +1/+1 counters on a creature, but only if it currently has no +1/+1 counters. This one-time growth effect can be reset if the counters are removed by some other effect, allowing you to adapt again.',
    rulesReference: '702.99',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.COUNTERS,
  },
  {
    name: 'Monstrosity',
    reminder: 'If this creature isn\'t monstrous, put N +1/+1 counters on it and it becomes monstrous.',
    explanation: 'Monstrosity is an activated ability that puts +1/+1 counters on a creature and makes it "monstrous" (a permanent designation). Once monstrous, the ability cannot be activated again. Many creatures with monstrosity have triggered abilities that fire "when this creature becomes monstrous," providing an additional bonus.',
    rulesReference: '701.30',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.COUNTERS,
  },
  {
    name: 'Renown',
    reminder: 'When this creature deals combat damage to a player, if it isn\'t renowned, put N +1/+1 counters on it and it becomes renowned.',
    explanation: 'Renown is a triggered ability that rewards a creature for dealing combat damage to a player. The first time it connects, it gets +1/+1 counters and becomes "renowned." Once renowned, renown does not trigger again. Some creatures gain additional abilities when they are renowned.',
    rulesReference: '702.111',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.COUNTERS,
  },
  {
    name: 'Fabricate',
    reminder: 'When this creature enters the battlefield, put N +1/+1 counters on it, or create N 1/1 colorless Servo artifact creature tokens.',
    explanation: 'Fabricate gives you a choice when the creature enters the battlefield: either make the creature bigger with +1/+1 counters, or create Servo tokens to go wide. This flexibility lets you adapt to the game state: counters when you need a single large threat, tokens when you need multiple bodies.',
    rulesReference: '702.122',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.COUNTERS,
  },
  {
    name: 'Modular',
    reminder: 'This creature enters the battlefield with N +1/+1 counters on it. When it dies, you may put its +1/+1 counters on target artifact creature.',
    explanation: 'Modular has two parts: the creature enters with a set number of +1/+1 counters, and when it dies, you can move all its +1/+1 counters onto a target artifact creature. This creates a chain where dying modular creatures transfer their power to surviving artifact creatures, making your board progressively stronger.',
    rulesReference: '702.42',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.COUNTERS,
  },
  {
    name: 'Afflict',
    reminder: 'Whenever this creature becomes blocked, defending player loses N life.',
    explanation: 'Afflict is a triggered ability that punishes the defending player for blocking. Whenever a creature with afflict becomes blocked, the defending player loses life equal to the afflict value regardless of what happens in combat. This creates a lose-lose situation: either take combat damage or block and still lose life.',
    rulesReference: '702.129',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Annihilator',
    reminder: 'Whenever this creature attacks, defending player sacrifices N permanents.',
    explanation: 'Annihilator is a devastating triggered ability found on Eldrazi creatures. When the creature attacks, the defending player must sacrifice permanents equal to the annihilator value before they even declare blockers. This strips the defending player of resources even if they manage to block or remove the attacker.',
    rulesReference: '702.85',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Exalted',
    reminder: 'Whenever a creature you control attacks alone, that creature gets +1/+1 until end of turn.',
    explanation: 'Exalted triggers whenever a creature you control attacks alone (as the only attacker). Each instance of exalted triggers separately, so multiple permanents with exalted each give +1/+1. The bonus goes to the attacking creature, not the permanent with exalted. This encourages a strategy of attacking with a single powerful creature.',
    rulesReference: '702.82',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Battle Cry',
    reminder: 'Whenever this creature attacks, each other attacking creature gets +1/+0 until end of turn.',
    explanation: 'Battle cry is a triggered ability that pumps all other attacking creatures when the creature with battle cry attacks. Each other attacker gets +1/+0 until end of turn. Multiple instances of battle cry stack, making wide attacks with many creatures more effective.',
    rulesReference: '702.90',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Flanking',
    reminder: 'Whenever this creature becomes blocked by a creature without flanking, the blocking creature gets -1/-1 until end of turn.',
    explanation: 'Flanking triggers when the creature is blocked by a creature without flanking. The blocker gets -1/-1 until end of turn, which can kill small blockers before damage is dealt. Multiple instances of flanking stack, so a creature with flanking blocked by a 2/2 without flanking would reduce it to 1/1 (or 0/0 with double flanking).',
    rulesReference: '702.24',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Bushido',
    reminder: 'Whenever this creature blocks or becomes blocked, it gets +N/+N until end of turn.',
    explanation: 'Bushido is a triggered ability that gives the creature a power and toughness boost whenever it blocks or is blocked. The bonus lasts until end of turn. Multiple instances of bushido stack. This makes bushido creatures significantly stronger in combat than their base stats suggest.',
    rulesReference: '702.44',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Shadow',
    reminder: 'This creature can block or be blocked by only creatures with shadow.',
    explanation: 'Shadow creates a separate combat dimension. Creatures with shadow can only block and be blocked by other creatures with shadow. This makes shadow creatures effectively unblockable against opponents without shadow creatures, but they also cannot block non-shadow attackers. Shadow creatures exist in their own parallel combat layer.',
    rulesReference: '702.27',
    category: KEYWORD_CATEGORIES.EVASION,
    theme: KEYWORD_THEMES.EVASION,
  },
  {
    name: 'Fear',
    reminder: 'This creature can\'t be blocked except by artifact creatures and/or black creatures.',
    explanation: 'Fear is an older evasion ability that prevents a creature from being blocked except by artifact creatures or black creatures. It has been functionally replaced by intimidate and then menace in modern sets, but still appears on older cards. Fear is strong against decks without black creatures or artifacts.',
    rulesReference: '702.35',
    category: KEYWORD_CATEGORIES.EVASION,
    theme: KEYWORD_THEMES.EVASION,
  },
  {
    name: 'Intimidate',
    reminder: 'This creature can\'t be blocked except by artifact creatures and/or creatures that share a color with it.',
    explanation: 'Intimidate is an evolution of fear that restricts blocking to artifact creatures or creatures sharing a color with the intimidate creature. A red creature with intimidate can only be blocked by red creatures or artifacts. Intimidate was itself later replaced by menace as the primary evasion mechanic.',
    rulesReference: '702.13',
    category: KEYWORD_CATEGORIES.EVASION,
    theme: KEYWORD_THEMES.EVASION,
  },
  {
    name: 'Skulk',
    reminder: 'This creature can\'t be blocked by creatures with greater power.',
    explanation: 'Skulk prevents a creature from being blocked by creatures with greater power. A 2/1 with skulk can only be blocked by creatures with power 2 or less. This makes skulk effective on small creatures, as opponents need to find equally small creatures to block them. Skulk rewards keeping power low.',
    rulesReference: '702.117',
    category: KEYWORD_CATEGORIES.EVASION,
    theme: KEYWORD_THEMES.EVASION,
  },
  {
    name: 'Horsemanship',
    reminder: 'This creature can\'t be blocked except by creatures with horsemanship.',
    explanation: 'Horsemanship is functionally identical to flying but exists as a separate ability from the Portal Three Kingdoms set. Creatures with horsemanship can only be blocked by other creatures with horsemanship. Since very few cards have horsemanship, it is effectively unblockable in most games.',
    rulesReference: '702.30',
    category: KEYWORD_CATEGORIES.EVASION,
    theme: KEYWORD_THEMES.EVASION,
  },
  {
    name: 'Phasing',
    reminder: 'This permanent phases in and out. While phased out, it\'s treated as though it doesn\'t exist.',
    explanation: 'A permanent with phasing phases out during its controller\'s untap step before untapping. While phased out, the permanent is treated as if it does not exist. It cannot be interacted with in any way. It phases back in during the next untap step. Phasing does not trigger enters- or leaves-the-battlefield abilities, making it distinct from exile-and-return effects.',
    rulesReference: '702.25',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.MISCELLANEOUS,
  },
  {
    name: 'Rampage',
    reminder: 'Whenever this creature becomes blocked, it gets +N/+N until end of turn for each creature blocking it beyond the first.',
    explanation: 'Rampage is a triggered ability that makes a creature stronger when multiply blocked. For each blocker beyond the first, the creature gets +N/+N where N is the rampage value. This discourages gang-blocking and makes rampage creatures dangerous to block with multiple creatures.',
    rulesReference: '702.22',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Banding',
    reminder: 'Any creatures with banding, and up to one without, can attack in a band. Bands are blocked as a group. If any creatures with banding you control are blocking or being blocked by a creature, you divide that creature\'s combat damage among any of the creatures it\'s being blocked by or is blocking.',
    explanation: 'Banding is one of the most complex mechanics in Magic. When attacking, creatures with banding can form a band that is blocked as a group. The key advantage is damage assignment: the banding player chooses how combat damage is distributed among their creatures, allowing you to protect important creatures by directing damage to expendable ones.',
    rulesReference: '702.21',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Ninjutsu',
    reminder: 'Return an unblocked attacker you control to hand: Put this card onto the battlefield from your hand tapped and attacking.',
    explanation: 'Ninjutsu is an activated ability used from your hand during combat. After an attacking creature is unblocked, you can return it to your hand and put the ninja onto the battlefield tapped and attacking. The ninja is already past the blocking step, so it deals combat damage. This creates surprise attackers and reuses enters-the-battlefield triggers on the returned creature.',
    rulesReference: '702.48',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Champion',
    reminder: 'When this creature enters the battlefield, sacrifice it unless you exile another [type] you control. When this creature leaves the battlefield, return the exiled card.',
    explanation: 'Champion requires you to exile another creature of a specified type when it enters the battlefield. If you cannot or choose not to, the champion creature is sacrificed. When the champion leaves the battlefield, the exiled creature returns. This creates a temporary upgrade that reverts when removed.',
    rulesReference: '702.71',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.MISCELLANEOUS,
  },
  {
    name: 'Soulshift',
    reminder: 'When this creature dies, you may return target Spirit card with mana value N or less from your graveyard to your hand.',
    explanation: 'Soulshift is a triggered ability that fires when the creature dies, letting you return a Spirit card of equal or lesser mana value from your graveyard to your hand. This creates a chain of recursion in Spirit-heavy decks where dying Spirits bring back smaller Spirits from the graveyard.',
    rulesReference: '702.45',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.RECURSION,
  },
  {
    name: 'Dash',
    reminder: 'You may cast this spell for its dash cost. If you do, it gains haste, and it\'s returned from the battlefield to its owner\'s hand at the beginning of the next end step.',
    explanation: 'Dash is an alternative cost that gives the creature haste but returns it to your hand at end of turn. This provides a temporary attacker that avoids sorcery-speed removal and can be recast each turn for repeated enters-the-battlefield triggers. The creature is only on the battlefield for one turn cycle.',
    rulesReference: '702.108',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Riot',
    reminder: 'This creature enters the battlefield with your choice of a +1/+1 counter or haste.',
    explanation: 'Riot gives you a choice when the creature enters the battlefield: either a +1/+1 counter for a permanently larger body, or haste for immediate attack potential. This flexibility lets you adapt to the game state: haste when you need to attack now, a counter when you want lasting value. If a creature has multiple instances of riot, you choose for each separately.',
    rulesReference: '702.135',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Blitz',
    reminder: 'If you cast this spell for its blitz cost, it gains haste and "When this creature dies, draw a card." Sacrifice it at the beginning of the next end step.',
    explanation: 'Blitz is an alternative cost similar to dash. The creature gains haste, draws a card when it dies, and is sacrificed at end of turn. Unlike dash, the creature goes to the graveyard (not your hand) and you draw a card. This makes blitz a burst of aggressive damage plus card advantage.',
    rulesReference: '702.158',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Wither',
    reminder: 'This deals damage to creatures in the form of -1/-1 counters.',
    explanation: 'Wither changes how a creature deals damage to other creatures. Instead of marking regular damage, it puts -1/-1 counters on the damaged creature. These counters are permanent and reduce the creature\'s power and toughness for the rest of the game unless removed. This makes wither creatures effective at permanently shrinking opposing creatures.',
    rulesReference: '702.79',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Infect',
    reminder: 'This creature deals damage to creatures in the form of -1/-1 counters and to players in the form of poison counters.',
    explanation: 'Infect combines wither (dealing damage as -1/-1 counters to creatures) with poison (dealing damage as poison counters to players). A player with 10 or more poison counters loses the game. Infect creatures effectively have a separate damage track for players, making them extremely dangerous as they only need to deal 10 total damage to win.',
    rulesReference: '702.89',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Toxic',
    reminder: 'Whenever this creature deals combat damage to a player, that player gets N poison counters.',
    explanation: 'Toxic is a triggered ability that gives poison counters to a player when the creature deals combat damage. Unlike infect, toxic deals regular damage AND gives poison counters. The poison is a bonus on top of normal damage. A player with 10 or more poison counters loses the game. Toxic creatures contribute to both normal and poison victory conditions.',
    rulesReference: '702.164',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Connive',
    reminder: 'Draw a card, then discard a card. If you discarded a nonland card, put a +1/+1 counter on this creature.',
    explanation: 'Connive is a keyword action that draws a card and then discards a card. If the discarded card is a nonland card, the conniving creature gets a +1/+1 counter. This provides card filtering (improving hand quality) while potentially growing the creature. Discarding a land gives you the filtering without the counter.',
    rulesReference: '702.154',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.CARD_FILTERING,
  },
  {
    name: 'Casualty',
    reminder: 'As an additional cost to cast this spell, you may sacrifice a creature with power N or greater. When you do, copy this spell.',
    explanation: 'Casualty is an optional additional cost where you sacrifice a creature of sufficient power to copy the spell. If you pay the casualty cost, you get a copy of the spell on the stack with the same targets (which you can change). This turns expendable creatures into spell duplication.',
    rulesReference: '702.153',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.COST_REDUCTION,
  },
  {
    name: 'Discover',
    reminder: 'Exile cards from the top of your library until you exile a nonland card with mana value N or less. Cast it without paying its mana cost or put it into your hand. Put the rest on the bottom in a random order.',
    explanation: 'Discover is similar to cascade but with a key difference: instead of being forced to cast the found card, you can choose to put it into your hand instead. This gives you more control over what happens, avoiding situations where you are forced to cast an unwanted spell. Like cascade, it only finds nonland cards with lower mana value.',
    rulesReference: '702.165',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Collect Evidence',
    reminder: 'Exile cards with total mana value N or greater from your graveyard.',
    explanation: 'Collect evidence is a keyword action used as a cost or condition. You exile cards from your graveyard whose total mana value meets or exceeds the required amount. This fuels effects by spending graveyard resources, similar to delve but more flexible since you choose specific cards to reach the total.',
    rulesReference: '702.166',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.GRAVEYARD,
  },
  {
    name: 'Suspect',
    reminder: 'A suspected creature has menace and can\'t block.',
    explanation: 'Suspect is a keyword action that gives a creature the "suspected" designation. A suspected creature gains menace (can\'t be blocked except by two or more creatures) but loses the ability to block. This creates an aggressive threat that can push damage through but leaves you vulnerable on defense.',
    rulesReference: '702.167',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Saddle',
    reminder: 'Tap any number of other creatures you control with total power N or greater: This Mount becomes saddled until end of turn.',
    explanation: 'Saddle is an activated ability on Mount creatures. You tap other creatures with total power meeting the saddle requirement to make the Mount "saddled" until end of turn. Saddled Mounts typically have enhanced abilities or can attack more effectively. The tapped creatures do not need to attack.',
    rulesReference: '702.174',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.COMBAT,
  },
  {
    name: 'Bargain',
    reminder: 'You may sacrifice an artifact, enchantment, or token as you cast this spell.',
    explanation: 'Bargain is an optional additional cost where you sacrifice an artifact, enchantment, or token to enhance the spell. If you pay the bargain cost, the spell gains an additional or improved effect. This turns surplus permanents into spell upgrades.',
    rulesReference: '702.170',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.COST_REDUCTION,
  },
  {
    name: 'Offspring',
    reminder: 'You may pay an additional {1} as you cast this spell. If you do, when this creature enters the battlefield, create a 1/1 copy of it.',
    explanation: 'Offspring is an optional additional cost of {1} that creates a 1/1 token copy of the creature when it enters the battlefield. The token has all the abilities of the original but is always 1/1 regardless of the original\'s power and toughness. This provides an extra body and doubles enters-the-battlefield triggers.',
    rulesReference: '702.176',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.COST_REDUCTION,
  },
  {
    name: 'Impending',
    reminder: 'If you cast this spell for its impending cost, it enters with N time counters and isn\'t a creature until they\'re all removed. At the beginning of your end step, remove a time counter.',
    explanation: 'Impending is an alternative cost that lets you cast an enchantment creature for less mana. It enters with time counters and is not a creature until all counters are removed (one per end step). While it has counters, it functions as a non-creature enchantment with its static and triggered abilities but cannot attack or block.',
    rulesReference: '702.177',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Spree',
    reminder: 'Choose one or more additional costs. You may choose each only once.',
    explanation: 'Spree is a casting mechanic where the spell has multiple optional additional costs, each unlocking a different mode. You must choose at least one mode (and pay its cost) but can choose multiple. Each mode you pay for adds its effect to the spell, creating a customizable spell that scales with your mana investment.',
    rulesReference: '702.175',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Gift',
    reminder: 'You may give a gift to an opponent as you cast this spell. If you do, [enhanced effect].',
    explanation: 'Gift is an optional cost where you give an opponent something beneficial (such as drawing a card or creating a token) when casting the spell. In exchange, your spell gets an enhanced or additional effect. This creates a political negotiation element, especially in multiplayer games.',
    rulesReference: '702.178',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Crew',
    reminder: 'Tap any number of other creatures you control with total power N or greater: This Vehicle becomes an artifact creature until end of turn.',
    explanation: 'Crew is an activated ability on Vehicle artifacts. You tap creatures with total power meeting the crew requirement to turn the Vehicle into an artifact creature until end of turn. Vehicles have power and toughness but are not creatures unless crewed. The creatures tapping to crew do not need to attack.',
    rulesReference: '702.121',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.MISCELLANEOUS,
  },
  {
    name: 'Ascend',
    reminder: 'If you control ten or more permanents, you get the city\'s blessing for the rest of the game.',
    explanation: 'Ascend checks if you control ten or more permanents. If you do, you get the city\'s blessing, a permanent game designation that lasts for the rest of the game even if your permanent count drops below ten. Many cards have enhanced abilities while you have the city\'s blessing.',
    rulesReference: '702.130',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.MISCELLANEOUS,
  },
  {
    name: 'Amass',
    reminder: 'Put N +1/+1 counters on an Army you control. If you don\'t control one, create a 0/0 black Zombie Army creature token first.',
    explanation: 'Amass creates or grows an Army creature token. If you already control an Army, amass adds +1/+1 counters to it. If you do not, it creates a 0/0 Zombie Army token first, then adds the counters. This creates a single growing threat that gets larger each time you amass. Later versions specify a creature type (e.g., amass Orcs).',
    rulesReference: '702.136',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.COUNTERS,
  },
  {
    name: 'Extort',
    reminder: 'Whenever you cast a spell, you may pay {W/B}. If you do, each opponent loses 1 life and you gain that much life.',
    explanation: 'Extort is a triggered ability that triggers whenever you cast any spell. You may pay one white or black mana to drain each opponent for 1 life, gaining that much life yourself. In multiplayer games, extort drains each opponent separately, making it very efficient. The {W/B} payment is not part of the spell\'s cost.',
    rulesReference: '702.100',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.MANA_AND_RESOURCES,
  },
  {
    name: 'Exploit',
    reminder: 'When this creature enters the battlefield, you may sacrifice a creature.',
    explanation: 'Exploit is a triggered ability that lets you sacrifice a creature (including the creature with exploit itself) when it enters the battlefield. Cards with exploit typically have a second triggered ability that fires when they exploit a creature, providing the actual payoff. This rewards sacrificing expendable creatures for value.',
    rulesReference: '702.109',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.MISCELLANEOUS,
  },
  {
    name: 'Devour',
    reminder: 'As this creature enters the battlefield, you may sacrifice any number of creatures. This creature enters the battlefield with N +1/+1 counters for each creature sacrificed.',
    explanation: 'Devour lets you sacrifice creatures as the devour creature enters the battlefield. For each creature sacrificed, it gets +1/+1 counters equal to the devour value. This turns small tokens into a single massive threat. A creature with devour 2 sacrificing three creatures would enter with six +1/+1 counters.',
    rulesReference: '702.81',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.COUNTERS,
  },
  {
    name: 'Training',
    reminder: 'Whenever this creature attacks with another creature with greater power, put a +1/+1 counter on this creature.',
    explanation: 'Training is a triggered ability that puts a +1/+1 counter on the creature whenever it attacks alongside a creature with greater power. This represents the smaller creature learning from the larger one. Training triggers once per attack if the condition is met, allowing the creature to grow over multiple combats.',
    rulesReference: '702.149',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.COUNTERS,
  },
  {
    name: 'Hideaway',
    reminder: 'When this permanent enters the battlefield, look at the top N cards of your library, exile one face down, then put the rest on the bottom. You may play the exiled card under certain conditions.',
    explanation: 'Hideaway lets you look at cards from the top of your library, exile one face down, and put the rest on the bottom. The permanent then has a condition under which you can play the hidden card, often for free. This creates a hidden threat that your opponent cannot see until the condition is met.',
    rulesReference: '702.74',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.CARD_FILTERING,
  },
  {
    name: 'Fortify',
    reminder: 'Attach to target land you control. Fortify only as a sorcery.',
    explanation: 'Fortify is the land equivalent of equip. Found on Fortification artifact cards, paying the fortify cost attaches the Fortification to a target land you control, granting it additional abilities. Like Equipment, Fortifications stay on the battlefield when the fortified land is destroyed and can be moved to a different land.',
    rulesReference: '702.66',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.MISCELLANEOUS,
  },
  {
    name: 'Detain',
    reminder: 'Until your next turn, that permanent can\'t attack or block and its activated abilities can\'t be activated.',
    explanation: 'Detain is a keyword action that locks down a permanent until your next turn. A detained permanent cannot attack, block, or activate abilities. This provides temporary removal that is especially effective against creatures and planeswalkers. The detained status wears off at the beginning of your next turn.',
    rulesReference: '701.28',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.PROTECTION,
  },
  {
    name: 'Cipher',
    reminder: 'Then you may exile this spell card encoded on a creature you control. Whenever that creature deals combat damage to a player, its controller may cast a copy of the encoded card without paying its mana cost.',
    explanation: 'Cipher lets you encode a spell onto a creature after it resolves. Whenever the encoded creature deals combat damage to a player, you may cast a free copy of the encoded spell. This turns a creature into a repeatable spell effect for as long as it keeps hitting opponents. The encoded card stays in exile.',
    rulesReference: '702.98',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Populate',
    reminder: 'Create a token that\'s a copy of a creature token you control.',
    explanation: 'Populate is a keyword action that copies one of your existing creature tokens. You choose which creature token to copy and create a new token that is an identical copy. This is most powerful when you have large or valuable creature tokens, as populate duplicates whatever token you already have on the battlefield.',
    rulesReference: '701.29',
    category: KEYWORD_CATEGORIES.ACTIVATED,
    theme: KEYWORD_THEMES.MISCELLANEOUS,
  },
  {
    name: 'Prepare',
    reminder: 'When this creature enters the battlefield prepared, a copy of its prepare spell is created in exile. You may cast that copy without paying its mana cost. Doing so unprepares this creature.',
    explanation: 'Prepare is a two-part frame mechanic. When a creature enters the battlefield via its prepare spell, a copy of that spell is created in exile. You may then cast that copy for free, which unprepares the creature. The exile copy persists until it is cast or until the creature leaves the battlefield or otherwise becomes unprepared. Casting the copy follows normal timing rules (you need to have priority and be in a window that allows casting creatures). Being prepared is not a copiable value, so copies of a prepared creature enter the battlefield unprepared with no exile copy.',
    rulesReference: '722',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Increment',
    reminder: 'Whenever you cast a spell, if the amount of mana you spent to cast it is greater than this creature\'s power or toughness, put a +1/+1 counter on this creature.',
    explanation: 'Increment is a triggered ability that rewards casting expensive spells. The trigger fires if the total mana spent to cast a spell exceeds this creature\'s power OR its toughness (both conditions are not required, only one needs to be true). All mana spent counts, including additional costs such as kicker or escape costs. The condition is checked again at resolution (if the creature\'s power and toughness both increase past the threshold before the trigger resolves, it will no longer trigger). Increment resolves before the triggering spell.',
    rulesReference: '702.191',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.COUNTERS,
  },
  {
    name: 'Paradigm',
    reminder: 'When this spell resolves for the first time, exile it. At the beginning of each of your first main phases, you may cast a copy of this card from exile without paying its mana cost.',
    explanation: 'Paradigm is a recurring spell mechanic. The first time a paradigm spell resolves it exiles itself, then at the beginning of each of your first main phases you may cast a free copy of it from exile. The delayed trigger lasts until end of game. It does not expire after one use. Each turn you may choose not to cast the copy. If you choose not to, the copy ceases to exist for that turn (it does not accumulate). The original card remains exiled between turns and is the source of each subsequent copy.',
    rulesReference: '702.192',
    category: KEYWORD_CATEGORIES.STATIC,
    theme: KEYWORD_THEMES.ALTERNATIVE_CASTING,
  },
  {
    name: 'Infusion',
    reminder: '(Ability word. This ability triggers if you gained any life this turn.)',
    explanation: 'Infusion is an ability word (italicised text with no rules meaning of its own). It groups abilities that care whether you have gained any life during the current turn. Any amount of life gain qualifies, even a single point. The final life total at the time of resolution is irrelevant. Only whether life was gained matters.',
    rulesReference: 'N/A',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.MISCELLANEOUS,
  },
  {
    name: 'Opus',
    reminder: '(Ability word. Triggers when you cast an instant or sorcery spell, with a stronger effect if five or more mana was spent.)',
    explanation: 'Opus is a Prismari ability word with no rules meaning of its own. It groups triggered abilities that fire whenever you cast an instant or sorcery spell. Each Opus ability has an initial effect and an additional or enhanced effect if five or more mana was spent casting the triggering spell. All mana spent counts, including additional costs. Ward costs paid by opponents are excluded. Only the controller\'s mana expenditure is counted.',
    rulesReference: 'N/A',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.MISCELLANEOUS,
  },
  {
    name: 'Repartee',
    reminder: '(Ability word. Triggers when you cast an instant or sorcery spell that targets a creature.)',
    explanation: 'Repartee is a Silverquill ability word with no rules meaning of its own. It groups triggered abilities that fire whenever you cast an instant or sorcery spell that targets at least one creature. The ability triggers only once per spell regardless of how many creatures are targeted. Targets need not remain valid at resolution for the trigger to have fired. The trigger fires at cast time based on what was targeted then, not at resolution.',
    rulesReference: 'N/A',
    category: KEYWORD_CATEGORIES.TRIGGERED,
    theme: KEYWORD_THEMES.MISCELLANEOUS,
  },
];

module.exports = {
  KEYWORD_CATEGORIES,
  KEYWORD_THEMES,
  KEYWORD_GLOSSARY,
};
